import { Routes } from '@angular/router';

import { PublicRouteData } from '~core/navigation/route-meta';
import { AuthGuardService } from '~features/auth/auth-guard.service';
import { CorporateUserGuardService } from '~features/auth/corporate-user-guard.service';
import { CompanyPageComponent } from '~pages/public/company-page/company-page.component';
import { SignupCompleteComponent } from '~pages/public/signup-complete/signup-complete';
import { WelcomePageComponent } from '~pages/public/welcome-page/welcome-page.component';

const reuseComponentConfig = { shouldReuse: true };
const minimalPublicRouteData = { layout: 'public-minimal' } satisfies PublicRouteData;
const slimPublicRouteData = { layout: 'public-slim' } satisfies PublicRouteData;
const minimalPublicWithCompanyListData = {
  layout: 'public-minimal',
  waitForCompanyList: true,
} satisfies PublicRouteData;

export const PUBLIC_ROUTES: Routes = [
  { path: '', component: WelcomePageComponent },
  { path: 'welcome-page', component: WelcomePageComponent },
  {
    path: 'signup',
    loadComponent: () => import('~pages/public/signup-page/signup-page.component').then((m) => m.SignupPageComponent),
  },
  {
    path: 'requestnewpassword',
    loadComponent: () =>
      import('~pages/public/requestpassword-page/requestpassword-page.component').then(
        (m) => m.RequestpasswordPageComponent
      ),
  },
  {
    path: 'setnewpassword/:token',
    loadComponent: () =>
      import('~pages/public/resetpassword-page/resetpassword-page.component').then((m) => m.ResetpasswordPageComponent),
  },
  { path: 'companies/:slug', component: CompanyPageComponent },
  {
    path: 'companies/:slug/:notUsedRSSID',
    redirectTo: '/companies/:slug',
  },
  {
    path: 'blog/weeklyScreener',
    loadComponent: () =>
      import('~pages/public/weekly-screener-page/weekly-screener-page.component').then(
        (m) => m.WeeklyScreenerPageComponent
      ),
    data: reuseComponentConfig,
  },
  {
    path: 'company-screener',
    loadComponent: () =>
      import('~pages/public/company-screener-page/company-screener-page.component').then(
        (m) => m.CompanyScreenerPageComponent
      ),
    data: reuseComponentConfig,
  },
  {
    path: 'all-stocks',
    loadComponent: () =>
      import('~pages/public/all-stocks/all-stocks-page.component').then((m) => m.AllStocksPageComponent),
    data: reuseComponentConfig,
  },
  {
    path: 'datenschutz',
    loadComponent: () =>
      import('~pages/public/datenschutz-page/datenschutz-page.component').then((m) => m.DatenschutzPageComponent),
    data: reuseComponentConfig,
  },
  {
    path: 'impressum',
    loadComponent: () =>
      import('~pages/public/impressum-page/impressum-page.component').then((m) => m.ImpressumPageComponent),
    data: reuseComponentConfig,
  },
  {
    path: 'mifid2',
    loadComponent: () =>
      import('~pages/public/mifid-disclaimer-page/mifid-disclaimer-page.component').then(
        (m) => m.MifidDisclaimerPageComponent
      ),
    data: reuseComponentConfig,
  },
  {
    path: 'disclaimer',
    loadComponent: () =>
      import('~pages/public/legal-disclaimer-page/legal-disclaimer-page.component').then(
        (m) => m.LegalDisclaimerPageComponent
      ),
    data: reuseComponentConfig,
  },
  {
    path: 'cookies-denied',
    loadComponent: () =>
      import('~pages/public/cookies-denied-page/cookies-denied-page.component').then(
        (m) => m.CookiesDeniedPageComponent
      ),
    data: reuseComponentConfig,
  },
  {
    path: 'dd',
    loadComponent: () =>
      import('~pages/public/legal-disclaimer-page/legal-disclaimer-page.component').then(
        (m) => m.LegalDisclaimerPageComponent
      ),
    data: reuseComponentConfig,
  },
  {
    path: 'signupcomplete',
    component: SignupCompleteComponent,
    data: reuseComponentConfig,
  },
  {
    // Landing spot for a confirmation link the backend could not honour, so the
    // customer gets a page instead of the API's JSON envelope (ADR 0040).
    path: 'confirmation-failed',
    loadComponent: () =>
      import('~pages/public/confirmation-failed/confirmation-failed.component').then(
        (m) => m.ConfirmationFailedComponent
      ),
    data: reuseComponentConfig,
  },
  {
    path: 'corporate/dashboard',
    loadComponent: () =>
      import('~pages/public/corporate-dashboard-page/corporate-dashboard-page.component').then(
        (m) => m.CorporateDashboardPageComponent
      ),
    data: reuseComponentConfig,
    canActivate: [AuthGuardService, CorporateUserGuardService],
  },
  {
    path: 'user/dashboard',
    loadComponent: () =>
      import('~pages/public/customer-dashboard-page/customer-dashboard-page.component').then(
        (m) => m.CustomerDashboardPageComponent
      ),
    data: reuseComponentConfig,
    canActivate: [AuthGuardService],
  },
  {
    path: 'events',
    loadComponent: () => import('~pages/public/events-page/events-page.component').then((m) => m.EventsPageComponent),
    data: reuseComponentConfig,
  },
  {
    path: 'events/invite/:token',
    loadComponent: () =>
      import('~features/events/external-registration/external-event-registration.component').then(
        (m) => m.ExternalEventRegistrationComponent
      ),
    data: minimalPublicRouteData,
  },
  {
    // Public feedback survey. Slim branded layout (logo header + full footer)
    // — the form is anonymous, so the full auth-aware nav header is omitted.
    path: 'feedback/:eventId',
    loadComponent: () =>
      import('~pages/public/event-feedback-page/event-feedback-page.component').then(
        (m) => m.EventFeedbackPageComponent
      ),
    data: slimPublicRouteData,
  },
  {
    // Public, shareable results page. A server-side publish gate decides
    // whether results render or a "not yet available" state shows.
    path: 'feedback/:eventId/results',
    loadComponent: () =>
      import('~pages/public/event-results-page/event-results-page.component').then((m) => m.EventResultsPageComponent),
    data: slimPublicRouteData,
  },
  {
    path: 'conference/:conference-slug',
    loadComponent: () =>
      import('~pages/public/conference-page/conference-page.component').then((m) => m.ConferencePageComponent),
    data: reuseComponentConfig,
  },
  {
    path: 'conferences',
    loadComponent: () =>
      import('~pages/public/conference-list-page/conference-list-page.component').then(
        (m) => m.ConferenceListPageComponent
      ),
    data: reuseComponentConfig,
  },
  {
    path: 'podcasts',
    loadComponent: () =>
      import('~pages/public/podcasts-page/podcasts-page.component').then((m) => m.PodcastsPageComponent),
    data: reuseComponentConfig,
  },
  {
    path: 'podcasts/:companyTicker/:research_date/:language',
    loadComponent: () =>
      import('~pages/public/podcasts-page/podcasts-page.component').then((m) => m.PodcastsPageComponent),
    data: reuseComponentConfig,
  },
  {
    path: 'videos',
    loadComponent: () => import('~pages/public/videos-page/videos-page.component').then((m) => m.VideosPageComponent),
    data: reuseComponentConfig,
  },
  {
    path: 'events/registration/:date/:companyTicker',
    loadComponent: () =>
      import('~pages/public/event-registration-page/event-registration-page.component').then(
        (m) => m.EventRegistrationPageComponent
      ),
    data: minimalPublicWithCompanyListData,
  },
  {
    path: 'events/video/:date/:companyTicker',
    loadComponent: () =>
      import('~pages/public/event-video-page/event-video-page.component').then((m) => m.EventVideoPageComponent),
    data: reuseComponentConfig,
  },
  // auffangen der unbekannten seiten 404 seiten
  { path: '**', redirectTo: '/', data: reuseComponentConfig },
];
